import { useEffect, useState } from "react";
import { useAppSelector } from "app/hooks";
import { fetchClientUserProfileAPI, fetchClientProfileAPI } from "api/apiHelper";
import AssignedEmployeesTable from "features/client_portal/AssignedEmployeesTable";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import Card from "components/Card";

const ClientOverview = () => {
  const clientProfile = useAppSelector((state) => state.client);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientUserData, setClientUserData] = useState<any>(null);
  const [clientData, setClientData] = useState<any>(null);

  useEffect(() => {
    const fetchClientUserData = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        const [userResponse, clientResponse] = await Promise.all([
          fetchClientUserProfileAPI(),
          fetchClientProfileAPI().catch((_err: any): { data: null } => ({ data: null })) // Client endpoint might not exist, so catch error
        ]);
        setClientUserData(userResponse.data);
        if (clientResponse?.data) {
          setClientData(clientResponse.data);
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || "Failed to load client user information.");
      } finally {
        setLoading(false);
      }
    };

    const fetchClientData = async (): Promise<void> => {
      try {
        const response = await fetchClientProfileAPI();
        setClientData(response.data);
      } catch (err: any) {
        // Silently fail if client endpoint doesn't exist
        console.log("Client details endpoint not available");
      }
    };

    if (clientProfile.clientLoading) {
      fetchClientUserData();
    } else if (clientProfile.id) {
      // Use data from Redux if available
      setClientUserData({
        client_name: clientProfile.clientName,
        client_id: clientProfile.clientId,
        first_name: clientProfile.firstName,
        last_name: clientProfile.lastName,
        email: clientProfile.email,
        job_title: clientProfile.jobTitle,
        photo: clientProfile.photoUrl,
      });
      // Fetch client details separately
      fetchClientData();
    }
  }, [clientProfile]);

  if (loading && !clientUserData) return <Backdrop show={true} />;

  return (
    <div className="mt-5">
      {error && <ErrorBox errorMessage={error} />}

      {/* Client User and Client Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Client User Information */}
        <Card extra="p-4">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
            General Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {(clientProfile.photoUrl || clientUserData?.photo) ? (
                <img
                  src={clientProfile.photoUrl || clientUserData?.photo}
                  alt={clientProfile.fullName || clientUserData?.first_name + " " + clientUserData?.last_name || "User"}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-navy-700 flex items-center justify-center">
                  <i className="fas fa-user text-2xl text-gray-400"></i>
                </div>
              )}
              <div>
                <p className="font-semibold text-navy-700 dark:text-white">
                  {clientProfile.fullName || (clientUserData?.first_name + " " + clientUserData?.last_name) || "N/A"}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {clientProfile.email || clientUserData?.email || "N/A"}
                </p>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-200 dark:border-navy-700">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Job Title</p>
                  <p className="text-sm font-medium text-navy-700 dark:text-white">
                    {clientProfile.jobTitle || clientUserData?.job_title || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</p>
                  <p className="text-sm font-medium text-navy-700 dark:text-white">
                    {clientProfile.isActive ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Client Information */}
        <Card extra="p-4">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
            Client Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {(clientData?.logo) ? (
                <img
                  src={clientData.logo}
                  alt={clientProfile.clientName || clientUserData?.client_name || "Client"}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-navy-700 flex items-center justify-center">
                  <i className="fas fa-building text-2xl text-gray-400"></i>
                </div>
              )}
              <div>
                <p className="font-semibold text-navy-700 dark:text-white">
                  {clientProfile.clientName || clientUserData?.client_name || "N/A"}
                </p>
                {clientData?.website && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <a 
                      href={clientData.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      {clientData.website}
                    </a>
                  </p>
                )}
              </div>
            </div>
            <div className="pt-3 border-t border-gray-200 dark:border-navy-700">
              <div className="space-y-3">
                {(clientData?.city || clientData?.state) && (
                  <div className="grid grid-cols-2 gap-4">
                    {clientData?.city && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">City</p>
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {clientData.city}
                        </p>
                      </div>
                    )}
                    {clientData?.state && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">State</p>
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {clientData.state}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {clientData?.website && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Website</p>
                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                      <a 
                        href={clientData.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-blue-600 dark:hover:text-blue-400 break-all"
                      >
                        {clientData.website}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Assigned Employees Table */}
      <AssignedEmployeesTable />
    </div>
  );
};

export default ClientOverview;
